/**
 * Shared per-section metadata for the event setup journey — icons, deep-link
 * routes, buildable set, manual-only CTAs and status chip styles. Extracted so
 * the setup surfaces (the onboarding-panel setup track, previews) share one
 * source of truth without importing a page component.
 */
import { ElementType, ReactNode } from "react";
import {
  IconArmchair,
  IconCalendarEvent,
  IconChecklist,
  IconFileText,
  IconFilter,
  IconForms,
  IconQrcode,
  IconTarget,
} from "@tabler/icons-react";
import { createElement } from "react";
import { SetupSectionKey } from "@/types/SetupPlan";
import { MODULE_ICONS } from "@/components/EventSidebar/sidebarModules";
import { SECTION_MODULE } from "./sectionModules";

export { SECTION_MODULE };

/**
 * Sections that are sub-lanes of a module, not the module itself. They keep
 * their own name AND their own icon — a row called "Appointments" wearing the
 * programme list icon sends the user hunting for "Appointments" in a sidebar
 * that says "Programme". Sections that ARE their module take the module's word
 * (see the setupHub titles) and the module's icon.
 */
const SECTION_OWN_ICONS: Partial<Record<SetupSectionKey, ElementType>> = {
  ADDITIONAL_FIELDS: IconForms,
  FILTERS: IconFilter,
  // Appointments live under Programme but are their own product surface
  // (SLOTTING) — the calendar glyph the rest of the product uses for them.
  OFFERING: IconCalendarEvent,
};

/**
 * One icon per section, taken from its sidebar module. Derived from the KEYS OF
 * SECTION_MODULE — a `Record<SetupSectionKey, …>`, so the union is covered by
 * construction. (Mapping over SETUP_SECTION_POOL instead would silently yield
 * `undefined` for a key the pool forgot.)
 */
export const SECTION_ICONS: Record<SetupSectionKey, ReactNode> = Object.fromEntries(
  (Object.keys(SECTION_MODULE) as SetupSectionKey[]).map((key) => [
    key,
    createElement(SECTION_OWN_ICONS[key] ?? MODULE_ICONS[SECTION_MODULE[key]], {
      size: 18,
    }),
  ]),
) as Record<SetupSectionKey, ReactNode>;

/** Event-relative deep link per section ("I'll do it myself" / "Open"). */
export const SECTION_ROUTES: Record<SetupSectionKey, string> = {
  EMAILS: "messaging/mail-templates",
  WORKFLOWS: "automation/workflows",
  OFFERING: "slotting",
  WEBSITE: "website",
  GOALS: "goals",
  GUESTS: "guests/guestlist",
  ADDITIONAL_FIELDS: "guests/guestlist",
  FILTERS: "guests/guestlist",
  SEATING: "seating",
  CHECKIN: "check-in/devices",
  DOCUMENTS: "documents",
};

/**
 * Manual journeys: primary CTA label key + icon. For the VELO_ONLY sections
 * the CTA is a FALLBACK — rendered only while the Velo lane is unavailable
 * (AI off / read-only); with Velo present the conversational lane is the one
 * action.
 */
export const MANUAL_CTAS: Partial<
  Record<SetupSectionKey, { icon: typeof IconChecklist; labelKey: string }>
> = {
  GUESTS: { icon: IconChecklist, labelKey: "actions.importGuests" },
  GOALS: { icon: IconTarget, labelKey: "actions.setGoals" },
  ADDITIONAL_FIELDS: { icon: IconForms, labelKey: "actions.setUp" },
  FILTERS: { icon: IconFilter, labelKey: "actions.setUp" },
  SEATING: { icon: IconArmchair, labelKey: "actions.setUp" },
  CHECKIN: { icon: IconQrcode, labelKey: "actions.setUp" },
  DOCUMENTS: { icon: IconFileText, labelKey: "actions.setUp" },
};

export { BUILDABLE, VELO_ONLY } from "./sectionModules";
